import { requestIdleCallback } from "@theme/utilities";
import PaginatedList from "@theme/paginated-list";

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #progressObserver;

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("initialized", "");
    this.#updateProductProgress();
    this.#progressObserver?.disconnect();
    this.#progressObserver = new MutationObserver((records) => {
      const changed = records.some((record) => {
        if (record.type === "attributes") return true;
        if (record.target instanceof Element && record.target.matches('[ref="grid"]')) return true;
        return [...record.addedNodes, ...record.removedNodes].some(
          (node) => node instanceof Element && (
            node.matches('[data-collection-progress], [ref="grid"]') ||
            node.querySelector('[data-collection-progress], [ref="grid"]')
          )
        );
      });
      if (changed) this.#updateProductProgress();
    });
    this.#progressObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-total"],
    });
  }

  disconnectedCallback() {
    this.#progressObserver?.disconnect();
    super.disconnectedCallback();
  }

  #updateProductProgress() {
    const progress = this.querySelector("[data-collection-progress]");
    const grid = this.querySelector('[ref="grid"]');
    if (!progress || !grid) return;

    const total = Number(progress.dataset.total);
    if (!Number.isFinite(total) || total <= 0) return;

    const loaded = Math.min(total, grid.querySelectorAll(':scope > [ref="cards[]"]').length);
    const count = progress.querySelector("[data-collection-viewed]");
    const bar = progress.querySelector("progress");

    if (count && count.textContent !== String(loaded)) count.textContent = String(loaded);
    if (bar) {
      if (bar.max !== total) bar.max = total;
      if (bar.value !== loaded) bar.value = loaded;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#setLayout(target.value);
  }

  toggleFiltering() {
    const isOpen = this.getAttribute("show-filtering") === "true";
    this.setAttribute("show-filtering", !isOpen);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute("product-grid-view", value);

    requestIdleCallback(() => {
      sessionStorage.setItem(`product-grid-view`, value);
    });
  }
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}
